import { datadogLogs } from '@datadog/browser-logs';
import { PropertyErrorCategory, PropertyError, TourArrivalSource, CreatePropertyErrorArgs } from '../types/property';

type ApiErrorShape = {
  config?: { url?: string };
  request?: unknown;
  response?: {
    status?: number;
    data?: string | {
      errorCode?: string;
      message?: string;
    };
  };
};


export const PROPERTY_STORAGE_KEYS = {
  propertyId: 'pid',
  visitId: 'visit_id',
  multipleTour: 'is_multiple_tour',
} as const;

const CODES_BY_CATEGORY: Record<PropertyErrorCategory, string[]> = {
  missing_property: ['PROPERTY_NOT_FOUND'],
  inactive_property: [
    'PROPERTY_INACTIVE',
    'PROPERTY_LOCK_INACTIVE',
    'PROPERTY_CONFIGURATION_INVALID',
  ],
  tour_unavailable: [
    'PROPERTY_ACCESS_DENIED',
    'IDENTITY_VERIFICATION_REQUIRED',
    'TOUR_LOCATION_NOT_CONFIRMED',
    'OUTSIDE_TOUR_HOURS',
    'SCHEDULED_VISIT_NOT_ELIGIBLE',
    'ON_DEMAND_TOUR_UNAVAILABLE',
    'TOUR_UNAVAILABLE',
  ],
  invalid_visit_history: [
    'VISITOR_TOUR_STATE_INVALID',
    'SCHEDULED_VISIT_NOT_FOUND',
    'VISIT_HISTORY_CONFLICT',
  ],
  expired_session: ['SESSION_INVALID', 'SESSION_EXPIRED', 'CUSTOMER_NOT_FOUND'],
  stale_client_state: ['PROPERTY_ID_INVALID', 'PROPERTY_STATE_INVALID'],
  api_request_failed: ['PROPERTY_API_FAILED', 'PROPERTY_API_UNAVAILABLE'],
  unknown: ['PROPERTY_RESOLUTION_UNKNOWN'],
};

const CATEGORY_BY_ERROR_CODE: Record<string, PropertyErrorCategory> = Object.entries(CODES_BY_CATEGORY)
  .reduce((codes, [category, errorCodes]) => {
    errorCodes.forEach(errorCode => {
      codes[errorCode] = category as PropertyErrorCategory;
    });

    return codes;
  }, {} as Record<string, PropertyErrorCategory>);

const CATEGORY_BY_STATUS: Record<number, PropertyErrorCategory> = {
  400: 'stale_client_state',
  401: 'expired_session',
  403: 'tour_unavailable',
  404: 'missing_property',
  409: 'invalid_visit_history',
  422: 'stale_client_state',
};

const TITLE_KEY_BY_CATEGORY: Record<PropertyErrorCategory, string> = {
  missing_property: 'property_not_found_title',
  inactive_property: 'property_unavailable_title',
  tour_unavailable: 'tour_unavailable_title',
  stale_client_state: 'refresh_needed_title',
  expired_session: 'session_expired_title',
  invalid_visit_history: 'tour_restart_needed_title',
  api_request_failed: 'temporary_problem_title',
  unknown: 'something_went_wrong_title',
};

const MESSAGE_KEY_BY_CATEGORY: Record<PropertyErrorCategory, string> = {
  missing_property: 'property_not_found_message',
  inactive_property: 'property_unavailable_message',
  tour_unavailable: 'tour_unavailable_message',
  stale_client_state: 'refresh_needed_message',
  expired_session: 'session_expired_message',
  invalid_visit_history: 'tour_restart_needed_message',
  api_request_failed: 'temporary_problem_message',
  unknown: 'something_went_wrong_message',
};

const MESSAGE_KEY_BY_CODE: Record<string, string> = {
  PROPERTY_ID_INVALID: 'property_id_invalid_message',
  IDENTITY_VERIFICATION_REQUIRED: 'identity_verification_required_message',
  TOUR_LOCATION_NOT_CONFIRMED: 'tour_location_not_confirmed_message',
};

const RETRYABLE_CATEGORIES: PropertyErrorCategory[] = [
  'stale_client_state',
  'expired_session',
  'api_request_failed',
];

const RETRYABILITY_BY_ERROR_CODE: Record<string, boolean> = {
  PROPERTY_ID_INVALID: false,
  IDENTITY_VERIFICATION_REQUIRED: true,
  TOUR_LOCATION_NOT_CONFIRMED: true,
};

const categoryForError = (code: string, status?: number): PropertyErrorCategory => {
  if (CATEGORY_BY_ERROR_CODE[code]) return CATEGORY_BY_ERROR_CODE[code];
  if (!status || status >= 500) return 'api_request_failed';

  return CATEGORY_BY_STATUS[status] || 'unknown';
};

export const isRetryableCategory = (category: PropertyErrorCategory) =>
  RETRYABLE_CATEGORIES.includes(category);

const isRetryableError = (category: PropertyErrorCategory, code: string) =>
  RETRYABILITY_BY_ERROR_CODE[code] ?? isRetryableCategory(category);

export const createPropertyError = (error: unknown): PropertyError => {
  if (isPropertyError(error)) return error;

  const candidate = error as Partial<CreatePropertyErrorArgs & ApiErrorShape> | undefined;
  const isLocalError = Boolean(candidate
    && typeof candidate.code === 'string'
    && !candidate.response
    && !candidate.request
    && !candidate.config);
  const { status: responseStatus, data } = candidate?.response || {};
  const responseBody = typeof data === 'object' && data ? data : undefined;

  const code = isLocalError
    ? candidate!.code!
    : responseBody?.errorCode || 'PROPERTY_RESOLUTION_UNKNOWN';
  const message = isLocalError
    ? candidate?.message
    : typeof data === 'string' ? data : responseBody?.message;
  const status = isLocalError ? candidate?.status : responseStatus;
  const endpoint = isLocalError ? candidate?.endpoint : candidate?.config?.url;

  const category = categoryForError(code, status);

  return Object.assign(new Error(message || code), {
    name: 'PropertyError',
    category,
    code,
    status,
    endpoint,
    retryable: isRetryableError(category, code),
    titleKey: TITLE_KEY_BY_CATEGORY[category],
    messageKey: MESSAGE_KEY_BY_CODE[code] || MESSAGE_KEY_BY_CATEGORY[category],
  });
};

export const isPropertyError = (error: unknown): error is PropertyError => {
  if (!(error instanceof Error)) return false;

  const candidate = error as Partial<PropertyError>;
  return typeof candidate.category === 'string'
    && typeof candidate.code === 'string'
    && typeof candidate.retryable === 'boolean';
};

export const getTourArrivalSource = (isRetry = false): TourArrivalSource => {
  if (isRetry) return 'retry';

  const navigation = window.performance
    ?.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;

  if (navigation?.type === 'reload') return 'refresh';
  if (navigation?.type === 'back_forward') return 'browser_navigation';

  return localStorage.getItem(PROPERTY_STORAGE_KEYS.visitId)
    || localStorage.getItem(PROPERTY_STORAGE_KEYS.propertyId)
    ? 'repeat_tour'
    : 'new_tour';
};

export const getStoredVisitId = () => localStorage.getItem(PROPERTY_STORAGE_KEYS.visitId);

export const clearStoredPropertyContext = () => {
  Object.values(PROPERTY_STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
};

export const getPropertyAvailabilityErrorCode = (property: {
  propStatus?: string;
  lockStatus?: string;
  hub_serial_number?: string;
  smart_lock_serial_number?: string;
}): string | null => {
  if (property?.propStatus !== 'PROPERTY_AVAILABLE') return 'PROPERTY_INACTIVE';

  const hasUsableLock = property?.lockStatus === 'LOCK_ACTIVE'
    || Boolean(property?.hub_serial_number)
    || Boolean(property?.smart_lock_serial_number);

  return hasUsableLock ? null : 'PROPERTY_LOCK_INACTIVE';
};

export const logPropertyResolutionFailure = (args: {
  error: PropertyError;
  propertyId?: string | number;
  propertyName?: string;
  builderId?: number;
  customerId?: string | number;
  visitId?: string | null;
  arrivalSource: TourArrivalSource;
  attempt: number;
}) => {
  if (args.error.logged) return;
  args.error.logged = true;

  datadogLogs.logger.error('Property resolution failed', {
    errorCategory: args.error.category,
    applicationErrorCode: args.error.code,
    httpStatus: args.error.status,
    serverMessage: args.error.message,
    endpoint: args.error.endpoint,
    propertyId: args.propertyId,
    propertyName: args.propertyName,
    builderId: args.builderId,
    customerId: args.customerId,
    visitId: args.visitId || undefined,
    visitorType: args.visitId ? 'repeat' : 'first_time',
    arrivalSource: args.arrivalSource,
    attempt: args.attempt,
    route: window.location.pathname,
    referrer: document.referrer || undefined,
  });
};
